import { data, type ObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from '@amazon/vinyl-util'
import type { PlaylistEntry } from './PlaylistController'

/**
 * Where Play next and Add to queue put a computer file: right after the current entry (`next`),
 * after the files already queued behind it (`afterQueued`, the next track boundary), or after the
 * feed's last entry (`last`).
 */
export type LocalPlacement = 'next' | 'afterQueued' | 'last'

/** A queued computer file and the feed entry it plays after, by queue occurrence; null when last. */
export interface QueuedLocalFile {
    readonly entry: PlaylistEntry
    readonly after: string | null
}

/** The files to play, in order, right after a feed entry (`after`), or after the feed's end (null). */
export interface LocalGroup {
    readonly after: PlaylistEntry | null
    readonly files: readonly PlaylistEntry[]
}

/**
 * A step of the walk: an entry the queued files answer, `null` when the walk has ended, or the
 * feed entry the feed walks on from (`undefined` answer).
 */
export type LocalStep =
    | { readonly entry: PlaylistEntry | null }
    | { readonly entry?: undefined; readonly feedFrom: PlaylistEntry }

interface Group {
    anchor: PlaylistEntry
    files: PlaylistEntry[]
}

/**
 * The computer files Play next and Add to queue put into a feed that can't hold them (a cloud
 * queue never carries one): each played after the feed entry it was queued behind, or after the
 * feed's last entry. Every queued file is an entry of its own, so one song queued twice plays twice.
 */
export class QueuedLocalFiles {
    // Feed entry key → the entry and the files that play right after it.
    private readonly groups = new Map<string, Group>()
    private tail: PlaylistEntry[] = []
    // Each queued file → the key of the feed entry it follows; null for the files after the end.
    private readonly anchors = new Map<PlaylistEntry, string | null>()
    // The feed's last entry, once the walk went past it into the tail.
    private tailFrom: PlaylistEntry | null = null
    private readonly _queued$ = data<readonly QueuedLocalFile[]>([])

    /** Every queued file with the feed entry it follows, each group in play order, the tail last. */
    get queued$(): ObservableValue<readonly QueuedLocalFile[]> {
        return this._queued$
    }

    get empty(): boolean {
        return this.anchors.size === 0
    }

    queue(
        current: PlaylistEntry | null,
        files: readonly PlaylistEntry[],
        placement: LocalPlacement
    ): void {
        const queued = files.map((file) => ({ ...file }))
        if (placement === 'last' || current == null) {
            this.tail.push(...queued)
            for (const file of queued) this.anchors.set(file, null)
        } else {
            const key = this.anchors.has(current) ? this.keyIn(current) : keyOf(current)
            const group = key === null ? this.tail : this.groupFor(current, key).files
            const at =
                placement === 'afterQueued'
                    ? group.length
                    : this.anchors.has(current)
                      ? group.indexOf(current) + 1
                      : 0
            group.splice(at, 0, ...queued)
            for (const file of queued) this.anchors.set(file, key)
        }
        this.publish()
    }

    /** Drops a queued file; anything else changes nothing. */
    remove(file: PlaylistEntry): void {
        if (!this.anchors.has(file)) return
        this.detach(file)
        this.publish()
    }

    /**
     * Seats `groups`' files after their feed entries in the order given, as a reorder of the play
     * queue leaves them; queued files no group names stay where they were.
     */
    arrange(groups: readonly LocalGroup[]): void {
        for (const { files } of groups) for (const file of files) this.detach(file)
        for (const { after, files } of groups) {
            const key = after ? keyOf(after) : null
            const group = key === null ? this.tail : this.groupFor(after!, key).files
            group.push(...files)
            for (const file of files) this.anchors.set(file, key)
        }
        this.publish()
    }

    /** The feed entry `entry` plays after: itself for a feed entry; null past the end of the feed. */
    anchorOf(entry: PlaylistEntry): PlaylistEntry | null {
        if (!this.anchors.has(entry)) return entry
        const key = this.keyIn(entry)
        return key === null ? this.tailFrom : (this.groups.get(key)?.anchor ?? null)
    }

    next(from: PlaylistEntry): LocalStep {
        if (!this.anchors.has(from)) {
            const first = this.groups.get(keyOf(from))?.files.at(0)
            return first ? { entry: first } : { feedFrom: from }
        }
        const key = this.keyIn(from)
        const group = this.filesOf(key)
        const following = group.at(group.indexOf(from) + 1)
        if (following) return { entry: following }
        const anchor = key === null ? null : (this.groups.get(key)?.anchor ?? null)
        return anchor ? { feedFrom: anchor } : { entry: null }
    }

    /** The first file after the feed's last entry `last`, or null with none queued there. */
    enterTail(last: PlaylistEntry): PlaylistEntry | null {
        const first = this.tail.at(0) ?? null
        if (first) this.tailFrom = last
        return first
    }

    /** The entry before a queued file; undefined for a feed entry, whose feed knows. */
    previous(from: PlaylistEntry): Maybe<PlaylistEntry> {
        if (!this.anchors.has(from)) return undefined
        const group = this.filesOf(this.keyIn(from))
        const index = group.indexOf(from)
        if (index > 0) return group.at(index - 1) ?? null
        return this.anchorOf(from)
    }

    /** `entry`, or the last file queued after it when the walk steps back onto it. */
    lastAfter(entry: PlaylistEntry): PlaylistEntry {
        return this.groups.get(keyOf(entry))?.files.at(-1) ?? entry
    }

    clear(): void {
        this.groups.clear()
        this.tail = []
        this.anchors.clear()
        this.tailFrom = null
        this._queued$.value = []
    }

    private keyIn(file: PlaylistEntry): string | null {
        return this.anchors.get(file) ?? null
    }

    private groupFor(anchor: PlaylistEntry, key: string): Group {
        let group = this.groups.get(key)
        if (!group) {
            group = { anchor, files: [] }
            this.groups.set(key, group)
        }
        return group
    }

    private filesOf(key: string | null): PlaylistEntry[] {
        return key === null ? this.tail : (this.groups.get(key)?.files ?? [])
    }

    private detach(file: PlaylistEntry): void {
        if (!this.anchors.has(file)) return
        const group = this.filesOf(this.keyIn(file))
        group.splice(group.indexOf(file), 1)
        this.anchors.delete(file)
    }

    private publish(): void {
        const queued: QueuedLocalFile[] = []
        for (const [after, group] of this.groups)
            for (const entry of group.files) queued.push({ entry, after })
        for (const entry of this.tail) queued.push({ entry, after: null })
        this._queued$.value = queued
    }
}

/** How a queued file names the feed entry it follows: a cloud queue occurrence, else the entry id. */
export function keyOf(entry: PlaylistEntry): string {
    return entry.entityReferenceId ?? entry.id
}
